import {createGlobalState} from '@vueuse/core'
import {ref} from 'vue'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {IConversationTypeInterfacesMap} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {useConversationTypeStrategy} from '@toolify/client/src/modules/chat/strategies/ConversationTypeStrategy/composables/useConversationTypeStrategy'

type ConversationTypePayload = IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload']

function buildOpenConversationKey(conversationType: ConversationType, conversationTypePayload: ConversationTypePayload): string | null {
  const conversationKey = useConversationTypeStrategy(conversationType).getConversationKey(conversationTypePayload)
  return conversationKey ? `${conversationType}:${conversationKey}` : null
}

export const useOpenConversations = createGlobalState(() => {
  const openConversationCounts = ref<Map<string, number>>(new Map())

  function registerOpenConversation(conversationType: ConversationType, conversationTypePayload: ConversationTypePayload): () => void {
    const key = buildOpenConversationKey(conversationType, conversationTypePayload)
    if (!key) {
      return () => undefined
    }
    const counts = new Map(openConversationCounts.value)
    counts.set(key, (counts.get(key) ?? 0) + 1)
    openConversationCounts.value = counts
    let isRegistered = true
    return () => {
      if (!isRegistered) {
        return
      }
      isRegistered = false
      const nextCounts = new Map(openConversationCounts.value)
      const remaining = (nextCounts.get(key) ?? 1) - 1
      if (remaining > 0) {
        nextCounts.set(key, remaining)
      } else {
        nextCounts.delete(key)
      }
      openConversationCounts.value = nextCounts
    }
  }

  function isConversationOpen(conversationType: ConversationType, conversationTypePayload: ConversationTypePayload): boolean {
    const key = buildOpenConversationKey(conversationType, conversationTypePayload)
    return !!key && openConversationCounts.value.has(key)
  }

  return {
    registerOpenConversation,
    isConversationOpen,
  }
})
